import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

const inputCls = "w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0077B6]";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız alındı. En kısa sürede dönüş yapacağız.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <div className="container-max px-4 md:px-8 section-pad">
        <div className="overline mb-3">İletişim</div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mb-3">Bize Ulaşın</h1>
        <p className="text-slate-600 max-w-2xl">Sorularınız, toplu sipariş talepleriniz veya bayilik için bizimle iletişime geçin.</p>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          <div className="lg:col-span-4 space-y-4">
            {[
              { icon: Phone, title: "Telefon", val: settings.contact_phone, href: `tel:${settings.contact_phone}` },
              { icon: MessageCircle, title: "WhatsApp", val: "Anında sipariş & destek", href: `https://wa.me/${settings.contact_whatsapp}` },
              { icon: Mail, title: "E-posta", val: settings.contact_email, href: `mailto:${settings.contact_email}` },
              { icon: MapPin, title: "Çiftlik", val: settings.contact_address },
            ].map(({ icon: Icon, title, val, href }) => (
              <a key={title} href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                className="block bg-[#F4F7F9] rounded-2xl p-5 border border-slate-200 hover:border-[#0077B6]/30 transition-colors">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0077B6]/10 flex items-center justify-center text-[#0077B6]">
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="overline mb-1">{title}</div>
                    <div className="font-semibold">{val}</div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <form onSubmit={submit} className="lg:col-span-8 bg-[#F4F7F9] rounded-2xl p-8 border border-slate-200">
            <h2 className="font-display text-xl font-extrabold mb-5">İletişim Formu</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <input data-testid="contact-name" required placeholder="Adınız" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="contact-email" required type="email" placeholder="E-posta" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="contact-phone" placeholder="Telefon" className={inputCls + " md:col-span-2"} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea data-testid="contact-message" required placeholder="Mesajınız" rows={5} className={inputCls + " md:col-span-2"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <button data-testid="contact-submit" disabled={loading} className="btn-primary mt-5 disabled:opacity-60">
              {loading ? "Gönderiliyor..." : <>Gönder <Send size={15} /></>}
            </button>
          </form>
        </div>

        <div className="mt-14 rounded-2xl overflow-hidden border border-slate-200 aspect-[16/7]">
          <iframe
            title="Harita"
            src="https://www.openstreetmap.org/export/embed.html?bbox=26.0%2C36.5%2C45.0%2C42.0&layer=mapnik"
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}
